import { NormalBlending, type Scene } from 'three';
import { VisualRandom } from '../../visual/index.js';
import {
  DEFAULT_PALETTE_ID,
  paletteColor,
  resolvePalette,
  type VisualPalette,
} from '../../visual/index.js';
import type {
  QualityContext,
  WaveEvent,
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
} from '../../visual/index.js';
import { InstancedField, NOISE_GLSL } from './materials/InstancedField.js';

/**
 * Watercolour.
 *
 * The obvious way to build this would have been to take `SumiRenderer` and give
 * it hues, and it would have been wrong: sumi and watercolour are not the same
 * material in two colours. Sumi is a *mark* — a loaded brush pressed against a
 * sheet, and what it leaves is settled the moment it lands. Watercolour is a
 * *fluid* — what it leaves is settled over the following seconds, by water the
 * hand no longer controls. So this renderer has no strokes in it at all.
 *
 * What it has instead:
 *
 *   - **Washes that keep moving after they land.** A wash grows towards a radius
 *     it approaches but never reaches, so it visibly slows to a stop. That
 *     deceleration *is* the drying, and it is the difference between paint that
 *     is still wet and paint that is finished.
 *   - **Edge darkening.** The signature of the material. Water leaves fastest at
 *     the boundary and carries pigment there, so a wash is thin in the middle and
 *     concentrated in a ring at its rim — the opposite of a brush mark, which is
 *     densest along its spine. It is why a wash reads as something that was once
 *     a puddle rather than as something that was once a gesture.
 *   - **Wet-on-wet mixing.** Two washes that meet make a third colour rather than
 *     one covering the other, because every wash is transparent and the pair is
 *     read through. Pigment accumulating towards white is the one result that
 *     would give the material away, so nothing here ever adds light.
 *   - **Granulation.** Heavy pigment settles into the paper's tooth as the water
 *     goes, so the grain strengthens with dryness instead of being a constant
 *     texture laid over everything.
 *   - **A cauliflower boundary.** Two scales of warp, the finer one coming up as
 *     the wash dries — the backrun a wash gets when it stops moving.
 *
 * There is no paper under any of this. Pigment falls to **transparent** at its
 * edge, never towards white: a wash's margin is *less pigment*, and less pigment
 * against this ground means less of it, not a paler ring.
 */

const WASH_FRAGMENT = /* glsl */ `
precision highp float;

${NOISE_GLSL}

uniform float uTime;
uniform float uDetail;
uniform float uBleed;
uniform float uGrain;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

void main() {
  vec2 p = (vUv - 0.5) * 2.0;
  float seed = vLife.z;
  float water = vShape.x;
  float grain = vShape.y;
  float rimLoad = vShape.z;
  float dry = vShape.w;

  // The boundary a puddle actually has: lobed at a large scale by where the
  // water ran, fretted at a small scale by the paper. The fine scale comes up as
  // the wash dries, which is the backrun that appears when it stops moving.
  // Water does not spread evenly: it runs further one way than the other,
  // depending on how the sheet lay. Without this every wash is a disc, and a
  // field of soft discs reads as bokeh rather than as paint.
  vec2 q = vec2(p.x * (1.0 + (fract(seed * 0.71) - 0.5) * 0.7), p.y);
  float lobes = fbm(q * 1.6 + seed * 5.0, int(uDetail)) - 0.5;
  float fret = fbm(q * 5.5 + seed * 11.0, int(uDetail)) - 0.5;
  float r = length(q) + lobes * 0.55 + fret * 0.12 * (0.4 + dry);

  // How far past the boundary the pigment keeps going. A wet wash has a diffuse
  // edge; a dry one has the hard line watercolour is known for.
  float soft = clamp(mix(0.1, 0.5, water) * uBleed * (1.0 - dry * 0.55), 0.04, 0.6);

  // Same containment argument as the ink: everything past a radius of 1 is
  // outside the geometry and comes back cut along a straight line. The warp
  // displaces the boundary outward before the fade even begins, so the fade and
  // the warp both have to be paid for out of the same margin.
  float slack = 0.28 + 0.06 * (0.4 + dry);
  r *= (1.0 + soft) / max(0.94 - slack, 0.3);

  float inside = 1.0 - smoothstep(1.0 - soft, 1.0 + soft, r);
  if (inside <= 0.0) discard;

  // Edge darkening. Pigment migrates to where the water leaves, so the ring just
  // inside the boundary carries several times what the middle does — and a wash
  // laid wet, which had more water to carry it, banks more of it there.
  float rim = inside * smoothstep(0.55, 1.0, r) * mix(0.5, 0.9, water);

  // The middle is a thinner wash, and the wetter it was laid the thinner still:
  // water that spread far took the pigment out to the edge with it. Thinner, not
  // empty — an eight-to-one rim was a ring of pigment around a hole, and a field
  // of those reads as a petri dish rather than as paint.
  float body = inside * mix(0.5, 0.28, water);

  // Granulation. This is a drying effect, not a texture — the tooth of the paper
  // only shows once there is no longer enough water to hold pigment above it.
  float granule = fbm(p * 13.0 + seed * 3.0, int(uDetail)) - 0.5;
  float settled = 1.0 + grain * granule * (0.5 + dry * 1.2) * uGrain;

  float pigment = max((body + rim * rimLoad * 0.55) * settled, 0.0);

  // Pigment separation: the coarse fraction strands at the rim, so the boundary
  // is not simply more of the same colour. It only appears where the water
  // stopped, which is what makes a granulating pigment recognisable.
  vec3 tint = mix(vColor.rgb, vColor.rgb * vec3(1.25, 0.86, 0.78), rim * grain * 0.8);

  // Laid wet, then setting. A wash is at its most diffuse the moment it lands.
  float set = smoothstep(0.0, 0.12, vAge);
  float alpha = clamp(pigment * vColor.a * set, 0.0, 1.0);
  if (alpha < 0.004) discard;

  gl_FragColor = vec4(tint, alpha);
}
`;

interface Wash {
  active: boolean;
  x: number;
  y: number;
  born: number;
  lifetime: number;
  /** Where the water has reached so far. */
  radius: number;
  /** Where it would reach given forever. It never gets there; it dries first. */
  target: number;
  /** How fast it closes the gap, in reciprocal seconds. */
  spread: number;
  hue: number;
  /**
   * The pigment this wash carries.
   *
   * A colour rather than a hue, because it comes from a palette now. Kept
   * alongside `hue` rather than replacing it: an explicit `hue` parameter still
   * works, and the granulation shader wants a tint it can shift.
   */
  tint: [number, number, number];
  water: number;
  grain: number;
  rimLoad: number;
  pigment: number;
  /** Which way the water ran. */
  angle: number;
  seed: number;
  /** 0 while the paint is still moving, 1 once it has stopped. */
  dry: number;
}

export interface WatercolorRendererOptions {
  readonly scene: Scene;
  readonly layerId: string;
  readonly fieldRadius: number;
  readonly capacity?: number;
}

export class WatercolorRenderer implements VisualRenderer {
  readonly id = 'WATERCOLOR';

  private readonly scene: Scene;
  private readonly fieldRadius: number;
  private readonly baseCapacity: number;
  private capacity: number;
  private detail = 3;
  private bleedScale = 1;
  private grainScale = 1;

  private field: InstancedField | null = null;
  private washes: Wash[] = [];
  private enabled = true;

  /** How much water is on the sheet. Everything about a wash follows from it. */
  private wetness = 0.6;
  /** How fast the sheet is losing it. Silence dries the paper. */
  private drying = 0.12;
  private pigmentLoad = 0.6;
  private hueCentre = 0.55;
  private hueSpread = 0.12;
  /**
   * The colours this layer washes with.
   *
   * The same palette INK_PAINT uses, and that is the point of §7: one set of
   * pigments, two materials. What separates them is not the colour but what the
   * material *does* with it — ink lays it down dense and opaque, a wash carries
   * it in water and lets the ground show through.
   *
   * Before this, every wash in every run was a shade of cyan: the hue was
   * centred on 0.55 with a spread of at most 0.18, so the material had colour
   * but only ever one colour.
   */
  private palette: VisualPalette = resolvePalette(DEFAULT_PALETTE_ID, 0);
  private paletteSeed = 0;
  private paletteId: string | undefined = undefined;
  /** Which palette entry the next wash takes. Stepped, not drawn — see INK_PAINT. */
  private pigmentCursor = 0;
  /** True while a recipe names an explicit hue, which overrides the palette. */
  private usingHueParam = false;
  private washScale = 1;
  private lastInputTime = 0;

  private pending: {
    strength: number;
    scale: number;
    wet: number;
    /** Field units. Absent means "wherever this renderer likes". */
    at?: { x: number; y: number };
    /** A wave's own stream, so its washes do not depend on the layer's. */
    stream?: VisualRandom;
    hue?: number;
  }[] = [];
  private wavesDrawn = 0;
  private readonly mutationCounts: Record<string, number> = {};
  private lastTime = 0;

  constructor(private readonly options: WatercolorRendererOptions) {
    this.scene = options.scene;
    this.fieldRadius = options.fieldRadius;
    this.baseCapacity = options.capacity ?? 64;
    this.capacity = this.baseCapacity;
  }

  setQuality(quality: QualityContext): void {
    if (this.field === null) {
      this.capacity = Math.max(10, Math.round(this.baseCapacity * quality.capacityScale));
    }
    this.detail = Math.max(2, Math.min(6, Math.round(2 + quality.iterationScale * 1.4)));
    this.bleedScale = quality.preset === 'LIVE' ? 0.85 : quality.preset === 'HIGH' ? 1 : 1.2;
    this.grainScale = quality.preset === 'LIVE' ? 0.7 : quality.preset === 'HIGH' ? 1 : 1.35;
    this.field?.setUniform('uDetail', this.detail);
    this.field?.setUniform('uBleed', this.bleedScale);
    this.field?.setUniform('uGrain', this.grainScale);
  }

  initialize(context: VisualFrameContext): void {
    this.paletteSeed = context.random.seed;
    this.palette = resolvePalette(
      typeof context.layer.parameters.palette === 'string'
        ? context.layer.parameters.palette
        : undefined,
      this.paletteSeed,
    );
    if (this.field === null) {
      this.field = new InstancedField({
        scene: this.scene,
        name: `visual:WATERCOLOR:${this.options.layerId}`,
        capacity: this.capacity,
        fragmentShader: WASH_FRAGMENT,
        // Transparent glazing, which is how watercolour actually mixes: a wash
        // laid over a dry one lets it through and the eye reads the pair as a
        // third colour. Additive blending gets the mixing too, but it gets it by
        // *adding light* — three overlapping washes went to white, the bloom pass
        // found them, and the material stopped being paint and became a lamp.
        // Glazing cannot do that: the result never exceeds the pigments in it.
        blending: NormalBlending,
        renderOrder: 6,
        uniforms: {
          uDetail: { value: this.detail },
          uBleed: { value: this.bleedScale },
          uGrain: { value: this.grainScale },
        },
      });
      this.washes = Array.from({ length: this.capacity }, () => emptyWash());
    }
    this.field.attach();
    this.lastTime = context.time;
  }

  reset(): void {
    for (const wash of this.washes) wash.active = false;
    this.pending.length = 0;
    this.field?.clearAll();
    this.wetness = 0.6;
    this.drying = 0.12;
    this.pigmentLoad = 0.6;
    this.washScale = 1;
    this.wavesDrawn = 0;
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
  }

  handleSignal(event: VisualSignalEvent): void {
    // The bar lays a wash and the section floods one. The beat is deliberately
    // not a mark here: a wash per beat would be a swarm of dots, and this
    // material's whole claim is that what it puts down keeps moving afterwards.
    if (event.name === 'BAR') this.queue(event.strength * 0.8, 1, 0.75);
    else if (event.name === 'SECTION') this.queue(event.strength, 1.9, 0.95);
  }

  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    const struck = !item.sustained;
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    switch (item.action) {
      case 'SPAWN':
        if (struck) this.queue(item.strength, 0.8, 0.7);
        break;
      case 'SPLASH':
        // A miss is pigment dropped into standing water: small, loaded, and it
        // blooms outward hard because it lands into wet.
        if (struck) {
          for (let index = 0; index < 2; index += 1) this.queue(item.strength, 0.42, 1);
        }
        break;
      case 'BLEED':
        this.wetness = Math.min(1, this.wetness + force * 0.6);
        if (struck) this.queue(item.strength, 1.3, 1);
        break;
      case 'DISTORT':
        this.hueSpread = Math.min(0.22, this.hueSpread + force * 0.2);
        if (struck) this.queue(item.strength, 0.9, 0.85);
        break;
      case 'BREAK':
        // Drying. Hard edges, strong grain, washes that stop where they are.
        this.drying = Math.min(1, this.drying + force * 0.6);
        break;
      case 'FREEZE':
        this.drying = Math.min(1, this.drying + force * 0.9);
        this.wetness = Math.max(0, this.wetness - force * 0.5);
        break;
      case 'RELEASE':
        this.drying = Math.max(0.05, this.drying - force * 0.7);
        this.wetness = Math.min(1, this.wetness + force * 0.5);
        break;
      case 'SHIFT':
        // Small. This fires on every input, and a quarter turn per press put
        // every hue on the sheet within two bars — a set of pigments, not a
        // spectrum, is what makes a passage read as one painting.
        this.hueCentre = (this.hueCentre + force * 0.05) % 1;
        break;
      case 'ROTATE':
        this.hueCentre = (this.hueCentre + force * 0.02) % 1;
        break;
      case 'EXPAND':
      case 'DENSITY':
        this.washScale = Math.min(2.6, this.washScale + force * 0.5);
        break;
      case 'CONTRACT':
        this.washScale = Math.max(0.35, this.washScale - force * 0.5);
        break;
      default:
        break;
    }
  }

  update(context: VisualFrameContext): void {
    const field = this.field;
    if (field === null || !this.enabled) return;

    const delta = Math.min(0.1, Math.max(0, context.time - this.lastTime));
    this.lastTime = context.time;
    const signals = context.signals;

    // Water on the sheet tracks the music's body; the treble is the air moving
    // over it, which is what takes the water away.
    const wetTarget = 0.25 + signals.AMPLITUDE * 0.5 + signals.BASS * 0.35;
    this.wetness += (Math.min(1, wetTarget) - this.wetness) * Math.min(1, delta * 0.9);
    const quiet = context.time - this.lastInputTime;
    const dryTarget = 0.1 + signals.TREBLE * 0.4 + (quiet > 2.5 ? 0.45 : 0);
    this.drying += (dryTarget - this.drying) * Math.min(1, delta * 0.6);
    this.pigmentLoad += (0.3 + signals.MID * 0.8 - this.pigmentLoad) * Math.min(1, delta * 1.1);

    const hueParam = numeric(context.layer.parameters.hue, Number.NaN);
    if (Number.isFinite(hueParam)) this.hueCentre = hueParam;
    this.usingHueParam = Number.isFinite(hueParam);
    const requested = typeof context.layer.parameters.palette === 'string'
      ? context.layer.parameters.palette
      : undefined;
    if (requested !== this.paletteId) {
      this.paletteId = requested;
      this.palette = resolvePalette(requested, this.paletteSeed);
    }
    // A chaotic run reaches for more of the box. This is the one place the
    // artwork's state shows up as colour rather than as quantity.
    // Held narrow. A palette open to the whole wheel is not a set of pigments.
    this.hueSpread = Math.min(0.18, 0.05 + context.art.chaos * 0.13);

    for (const request of this.pending) this.spawn(context, request);
    this.pending.length = 0;

    const opacity = context.layer.opacity * context.layer.intensity;

    let live = 0;
    for (const wash of this.washes) {
      if (!wash.active) continue;
      const age = (context.time - wash.born) / wash.lifetime;
      if (age >= 1) {
        wash.active = false;
        continue;
      }

      // Asymptotic, not linear. A wash slows as it goes and stops without ever
      // reaching its target, which is what drying looks like from outside; linear
      // growth reads as an expanding circle, which is an explosion.
      const rate = wash.spread * (1 - this.drying * 0.75);
      wash.radius += (wash.target - wash.radius) * (1 - Math.exp(-rate * delta));
      // How finished it is: partly its own age, partly how dry the sheet is.
      wash.dry = clamp01(age * 0.8 + this.drying * 0.6);

      // Pigment does not evaporate. A wash holds its colour and the paper
      // releases it at the very end of its life, rather than fading throughout.
      const settle = age > 0.8 ? 1 - (age - 0.8) / 0.2 : 1;
      const tint = wash.tint;

      field.write(
        live,
        [wash.x, wash.y, wash.radius, wash.angle],
        [wash.born, wash.lifetime, wash.seed, 0],
        [tint[0], tint[1], tint[2], clamp01(wash.pigment * settle * opacity)],
        [clamp01(wash.water), wash.grain, wash.rimLoad, clamp01(wash.dry)],
      );
      live += 1;
      if (live >= field.capacity) break;
    }

    field.setTime(context.time);
    field.commit(live);
  }

  private spawn(
    context: VisualFrameContext,
    request: {
      strength: number;
      scale: number;
      wet: number;
      at?: { x: number; y: number };
      stream?: VisualRandom;
      hue?: number;
    },
  ): void {
    const wash = this.washes.find((candidate) => !candidate.active);
    if (wash === undefined) return;
    // A wave brings its own stream, so the same event lays the same wash
    // however much work this layer had already done when it arrived.
    const random = request.stream ?? context.random;
    const reach = this.fieldRadius * numeric(context.layer.parameters.reach, 0.6);

    // How wet the sheet was where this one landed. A wash laid into water spreads
    // much further than one laid onto dry paper, and everything else follows.
    const water = clamp01(this.wetness * request.wet * random.range(0.75, 1.15));

    wash.active = true;
    wash.born = context.time;
    wash.seed = random.next() * 100;
    wash.angle = random.range(0, Math.PI * 2);
    // Field units in, world units out.
    wash.x = request.at === undefined ? random.signed() * reach : request.at.x * this.fieldRadius;
    wash.y =
      request.at === undefined ? random.signed() * reach * 0.85 : request.at.y * this.fieldRadius;
    wash.water = water;
    wash.grain = random.range(0.3, 0.95);
    // A wetter wash banks more pigment at its rim, because more water crossed it.
    wash.rimLoad = clamp01(random.range(0.55, 1.05) * (0.5 + water * 0.7));
    wash.pigment = clamp01(
      (0.34 + this.pigmentLoad * 0.24 + request.strength * 0.18) * random.range(0.75, 1.2),
    );
    wash.hue = request.hue ?? (this.hueCentre + random.signed() * this.hueSpread + 1) % 1;
    wash.tint = this.washTint(wash.hue, wash.pigment);

    const base = this.fieldRadius * 0.13 * request.scale * this.washScale;
    wash.radius = base * random.range(0.28, 0.45);
    wash.target = base * random.range(1.6, 2.6) * (0.6 + water * 0.9);
    wash.spread = random.range(0.5, 1.1) * (0.35 + water * 1.3);
    wash.lifetime = random.range(8, 15) * (0.7 + water * 0.5);
    this.lastInputTime = context.time;
  }

  /**
   * The colour one wash carries.
   *
   * ## Why this is not the same call INK_PAINT makes
   *
   * Same palette, deliberately different treatment — that is §7. A wash is
   * *water carrying pigment*, so it takes the palette colour and thins it:
   * scaled down in value and pulled a little toward its own grey, which is what
   * dilution does. Ink lays the entry down at full strength and lifts it with
   * force. Put the same red through both and one is a red mass, the other is a
   * red stain with the ground visible through it.
   *
   * The thinning is also what keeps a wash saturated. The measured tone curve
   * destroys saturation above about 0.45 input luminance, and washes overlap —
   * three of them stacked at ink strength would sum past that and come back
   * white, which is the failure this material had before it had colour at all.
   */
  private washTint(hue: number, load: number): [number, number, number] {
    if (this.usingHueParam) return pigmentTone(hue, load);
    this.pigmentCursor += 1;
    const base = paletteColor(this.palette, this.pigmentCursor);
    // Dilution: less pigment per unit area than ink, and a touch less saturated,
    // because water between the particles is what a wash is.
    // Thinner than ink but not fainter than the material used to be. The first
    // attempt multiplied the palette entry by 0.52–0.82, which on top of an
    // already low-value palette put the washes below what the old fixed HSL
    // tone produced — the colour was right and nobody could see it.
    const thin = 0.95 + clamp01(load) * 0.5;
    const grey = ((base[0] as number) + (base[1] as number) + (base[2] as number)) / 3;
    const desaturate = 0.88;
    return [
      clamp01((grey + ((base[0] as number) - grey) * desaturate) * thin),
      clamp01((grey + ((base[1] as number) - grey) * desaturate) * thin),
      clamp01((grey + ((base[2] as number) - grey) * desaturate) * thin),
    ];
  }

  private queue(strength: number, scale: number, wet: number): void {
    if (this.pending.length > 12) return;
    this.pending.push({ strength, scale, wet });
  }

  /**
   * A wave, as pigment.
   *
   * Injection rather than impact: a loaded brush touched into standing water
   * at that point, and then the water carries it. So one large, very wet
   * wash goes in at the event and a short trail of smaller, drier ones is
   * laid *along the direction of travel* — which is what makes the direction
   * visible in a material that has no edges of its own to point with.
   *
   * The satellites share the leading wash's hue, so the trail reads as one
   * pigment spreading rather than as several unrelated drops; where it
   * crosses an earlier wave, the two mix and make a third colour.
   */
  spawnWave(event: WaveEvent): void {
    const stream = new VisualRandom(event.seed);
    const force = clamp01(event.strength) * event.scale;
    // One hue for the whole injection, offset from the palette centre by the
    // event rather than by the layer clock.
    const hue = (this.hueCentre + stream.signed() * this.hueSpread + 1) % 1;

    this.pending.push({
      strength: force,
      scale: event.style === 'torn' ? 1.5 : event.style === 'stray' ? 0.6 : 1.15,
      wet: event.style === 'stray' ? 0.55 : 1,
      at: { x: event.x, y: event.y },
      stream,
      hue,
    });

    // The trail. A torn wave throws further and drier.
    const satellites = event.style === 'torn' ? 3 : event.style === 'stray' ? 1 : 2;
    for (let index = 0; index < satellites; index += 1) {
      const along = (index + 1) / (satellites + 1);
      const drift = event.direction + stream.signed() * 0.55;
      const reach = (0.1 + along * 0.26) * event.scale;
      this.pending.push({
        strength: force * (1 - along * 0.45),
        scale: 0.5 + (1 - along) * 0.4,
        wet: 0.85 - along * 0.35,
        at: { x: event.x + Math.cos(drift) * reach, y: event.y + Math.sin(drift) * reach },
        stream,
        hue,
      });
    }
    this.wavesDrawn += 1;
  }

  render(): void {
    // Drawn by the stage's single render.
  }

  setParameters(): void {
    // Read per frame from the resolved layer state.
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.field !== null) this.field.visible = enabled;
    if (!enabled) this.reset();
  }

  dispose(): void {
    this.field?.dispose();
    this.field = null;
    this.washes = [];
  }

  get metrics(): {
    readonly live: number;
    readonly capacity: number;
    readonly wetness: number;
    readonly drying: number;
    readonly pigmentLoad: number;
    readonly hueSpread: number;
    /** Mean radius over target: how far the washes have actually run. */
    readonly diffusion: number;
    readonly waves: number;
    readonly mutations: Readonly<Record<string, number>>;
  } {
    let diffusion = 0;
    let counted = 0;
    for (const wash of this.washes) {
      if (!wash.active || wash.target <= 0) continue;
      diffusion += wash.radius / wash.target;
      counted += 1;
    }
    return {
      live: this.field?.liveCount ?? 0,
      capacity: this.capacity,
      wetness: Number(this.wetness.toFixed(3)),
      drying: Number(this.drying.toFixed(3)),
      pigmentLoad: Number(this.pigmentLoad.toFixed(3)),
      hueSpread: Number(this.hueSpread.toFixed(3)),
      diffusion: Number((counted === 0 ? 0 : diffusion / counted).toFixed(3)),
      waves: this.wavesDrawn,
      mutations: { ...this.mutationCounts },
    };
  }
}

/**
 * A pigment's colour at a given load.
 *
 * Kept under the bloom threshold on purpose, and by a margin that does not depend
 * on how many washes overlap. Saturation stays high and value stays low, which is
 * both what keeps the post chain from finding this and simply what a transparent
 * pigment looks like — watercolour has no white in it at all, only paper showing
 * through, and there is no paper here.
 */
function pigmentTone(hue: number, load: number): [number, number, number] {
  const value = 0.26 + clamp01(load) * 0.2;
  return hslToRgb(((hue % 1) + 1) % 1, 0.55, value);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;
  const sector = Math.floor(h * 6) % 6;
  const rgb: [number, number, number] =
    sector === 0 ? [c, x, 0]
    : sector === 1 ? [x, c, 0]
    : sector === 2 ? [0, c, x]
    : sector === 3 ? [0, x, c]
    : sector === 4 ? [x, 0, c]
    : [c, 0, x];
  return [rgb[0] + m, rgb[1] + m, rgb[2] + m];
}

function emptyWash(): Wash {
  return {
    active: false, x: 0, y: 0, born: 0, lifetime: 1, radius: 0, target: 0, spread: 1,
    hue: 0.55, tint: [0.25, 0.25, 0.3], water: 0.5, grain: 0.5, rimLoad: 0.7, pigment: 0.3, angle: 0, seed: 0, dry: 0,
  };
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
